import { CheckoutEventNames, PaddleEventData } from '@paddle/paddle-js';

/**
 * The glue both embedded Paddle checkouts need: the subscribe page's Paddle rail
 * (`subscribe-paddle-rail.ts`) and the become-a-coach page
 * (`pages/coach-checkout/coach-checkout-page.ts`).
 *
 * Each of them used to carry its own hand-kept copy of these pieces, and nothing
 * made the two change together (deferred finding `df-2290247721c2`). Only the
 * mechanics that were identical live here. What genuinely differs between the two
 * checkouts — what to do when the frame never renders, the problem names reported,
 * the open options, the email and completion handling — stays with each caller and
 * is handed in. See `design-docs/paddle-integration.md`.
 */

// The bare CSS class name Paddle.js mounts its inline frame into. Paddle's
// `settings.frameTarget` takes a class NAME (it does
// `getElementsByClassName(frameTarget)[0].appendChild(...)`), NOT a selector —
// a leading dot would match nothing and throw "appendChild of undefined".
export const kPaddleFrameClass = 'paddle-checkout-frame';
// The matching CSS selector (with the dot) for our own querySelector poll.
export const kPaddleFrameSelector = '.' + kPaddleFrameClass;

/** How often the frame-ready poll looks for the container again. */
export const kPaddleFramePollIntervalMs = 50;
/** The attempt number at which the poll gives up (about 1 second in all). */
export const kPaddleFramePollMaxAttempts = 20;

/** What a caller hands {@link openWhenPaddleFrameReady}. */
export interface PaddleFrameReadyHooks {
  /** Look for the frame container. Defaults to `document.querySelector(kPaddleFrameSelector)`. */
  findFrameTarget?: () => unknown;
  /** The page's safe timeout: cancelled on teardown, skipped once the view is gone. */
  schedule: (callback: () => void, delayMs: number) => void;
  /** Checked before every attempt, including the first; true stops the poll silently. */
  shouldStop?: () => boolean;
  /** The container exists: open the checkout into it. */
  onFrameReady: () => void;
  /** The container never rendered within the budget. Each checkout decides what the user sees. */
  onGiveUp: () => void;
}

/**
 * Wait for Angular to render the frame container, then call `onFrameReady`.
 *
 * The container is rendered by a template branch that only turns on in the same
 * tick the checkout is asked to open, so the first look usually misses. Retry every
 * {@link kPaddleFramePollIntervalMs} and give up at attempt
 * {@link kPaddleFramePollMaxAttempts}.
 */
export function openWhenPaddleFrameReady(hooks: PaddleFrameReadyHooks, attempt = 0): void {
  // if the caller says the page is gone (or has nothing to open), stop quietly
  if (hooks.shouldStop?.()) return;
  const target = hooks.findFrameTarget ? hooks.findFrameTarget() : document.querySelector(kPaddleFrameSelector);
  if (!target) {
    // if the budget is spent, hand the give-up to the caller; otherwise look again
    if (attempt >= kPaddleFramePollMaxAttempts) {
      hooks.onGiveUp();
      return;
    }
    hooks.schedule(() => openWhenPaddleFrameReady(hooks, attempt + 1), kPaddleFramePollIntervalMs);
    return;
  }
  hooks.onFrameReady();
}

/** What kind of failure a Paddle checkout error or failure event is (df-38f32a018053). */
export enum PaddleCheckoutErrorKind {
  /** The checkout's request never reached Paddle (offline, ad blocker, network filter). */
  Network = 'network',
  /** Paddle sent no type, code or detail at all, so the cause is unknown. */
  NoFields = 'no-fields',
  /** Anything else: a real Paddle-side fault worth a person's attention. */
  Other = 'other',
}

/** Paddle.js's own type / code when fetch itself threw. */
const kPaddleNetworkErrorCode = 'network_error';
/**
 * The two ways Paddle's checkout names a network failure in its `detail`, as seen in
 * Sentry (CYBERNETIC-FITNESS-APP-4FG): "Network Error", and "Something went wrong |
 * Failed to fetch". Matched case-insensitively anywhere in the detail.
 */
const kPaddleNetworkDetailPattern = /network error|failed to fetch/i;

/** Info report for a checkout error whose request never reached Paddle. */
export const kPaddleCheckoutNetworkErrorReport = 'PaddleCheckoutNetworkError';
/** Silent-problem report for a checkout failure Paddle sent with no fields at all. */
export const kPaddleCheckoutFailedNoFieldsReport = 'PaddleCheckoutFailedNoFields';

/** Which page an embedded Paddle checkout error came from, recorded in its report. */
export enum PaddleCheckoutPage {
  Subscribe = 'subscribe',
  CoachCheckout = 'coach-checkout',
}

/** The context a network-kind checkout error is reported with: Paddle's own fields. */
export function paddleCheckoutErrorContext(ev: PaddleEventData, page: PaddleCheckoutPage): Record<string, unknown> {
  return { page, event: ev.name, type: ev.type, code: ev.code, detail: ev.detail };
}

/** Decide what kind of failure one CHECKOUT_ERROR / CHECKOUT_FAILED event is. Pure. */
export function classifyPaddleCheckoutError(ev: PaddleEventData): PaddleCheckoutErrorKind {
  if (ev.type === kPaddleNetworkErrorCode || ev.code === kPaddleNetworkErrorCode) {
    return PaddleCheckoutErrorKind.Network;
  }
  if (ev.detail && kPaddleNetworkDetailPattern.test(ev.detail)) {
    return PaddleCheckoutErrorKind.Network;
  }
  // if Paddle sent nothing at all, say so rather than guessing a cause
  if (!ev.type && !ev.code && !ev.detail) {
    return PaddleCheckoutErrorKind.NoFields;
  }
  return PaddleCheckoutErrorKind.Other;
}

/** What a caller hands {@link dispatchPaddleCheckoutEvent}. */
export interface PaddleCheckoutEventHandlers {
  /** True once the page's view has been torn down (the NG0911 guard). */
  isViewGone: () => boolean;
  /**
   * CHECKOUT_ERROR / CHECKOUT_FAILED, with the kind {@link classifyPaddleCheckoutError}
   * gave it. Runs even on a torn-down page, so it must not touch the view.
   */
  reportCheckoutError: (ev: PaddleEventData, detail: string, kind: PaddleCheckoutErrorKind) => void;
  /** Hide the checkout spinner and mark the page for check. Only called while the view is alive. */
  stopSpinner: () => void;
  /** CHECKOUT_CUSTOMER_CREATED / CHECKOUT_CUSTOMER_UPDATED, with the email Paddle reports. */
  onCustomerEmail: (email: string | undefined) => void;
  /** CHECKOUT_COMPLETED: payment succeeded on Paddle's side; the grant still comes by webhook. */
  onCompleted: (ev: PaddleEventData) => void;
}

/**
 * Route one Paddle.js event to the caller's handlers.
 *
 * The ordering is the non-obvious part. Paddle keeps the `eventCallback` closure
 * alive after navigation, so an event can arrive on a page that is already gone. An
 * error or failure must still be REPORTED then, so it is handled before the
 * view-gone check, and only its spinner-clear is guarded. Every other event only
 * changes the live view, so it is dropped on a gone page.
 */
export function dispatchPaddleCheckoutEvent(ev: PaddleEventData, handlers: PaddleCheckoutEventHandlers): void {
  if (ev.name === CheckoutEventNames.CHECKOUT_ERROR || ev.name === CheckoutEventNames.CHECKOUT_FAILED) {
    // Paddle puts the error fields directly on the event (PaddleEventData extends
    // Partial<CheckoutEventError>): type / code / detail / documentation_url.
    const detail = ev.detail || ev.code || ev.type || 'unknown';
    handlers.reportCheckoutError(ev, detail, classifyPaddleCheckoutError(ev));
    // if the view is still alive, clear the spinner (markForCheck on a torn-down page is NG0911)
    if (!handlers.isViewGone()) {
      handlers.stopSpinner();
    }
    return;
  }
  // if the page is gone, the remaining events have nothing live to update
  if (handlers.isViewGone()) return;
  switch (ev.name) {
    case CheckoutEventNames.CHECKOUT_LOADED:
      handlers.stopSpinner();
      break;
    case CheckoutEventNames.CHECKOUT_CUSTOMER_CREATED:
    case CheckoutEventNames.CHECKOUT_CUSTOMER_UPDATED:
      handlers.onCustomerEmail(ev.data?.customer?.email);
      break;
    case CheckoutEventNames.CHECKOUT_COMPLETED:
      handlers.onCompleted(ev);
      break;
    default:
      break;
  }
}
